import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  useDataProvider,
  useNotify,
  useRefresh,
  type Identifier,
} from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import type { Capability } from '../auth/permissions';
import { performStatusTransition } from './performStatusTransition';

/**
 * A guarded action that captures a free-text REASON (and optionally a member id, a
 * structured reason code, or a suspension period) BEFORE writing, then routes
 * through the same performStatusTransition chokepoint every guarded action uses
 * (capability-checked, audited for free). The captured fields ride in the write
 * patch, so they land in the audit trail. Shared by the manual-attribution queue
 * (the clickless credit and reject, D-manual-attribution), the fraud queue
 * (suspend / ban / escalate / reinstate, D-fraud-queue) and the suspension flow
 * (suspend with a reason code and a period, dismiss an appeal, D-suspension-flow),
 * where a recorded WHY is exactly the point.
 *
 * The reason-code and period selects are OPTIONAL and orthogonal: attribution's
 * free-text-only usage passes neither and is unchanged. `buildData` always receives
 * the same four arguments (justification, memberId, reasonCode, periodDays); a
 * caller that wants none of the extras just ignores the trailing ones.
 *
 * Actions with no free input (a candidate credit, a flag) use the plain
 * TransitionButton instead.
 */
export interface ReasonCodeOption {
  value: string;
  label: string;
}

export interface PeriodOption {
  /** The period length in days, written into the patch via `buildData`. */
  value: number;
  label: string;
}

export interface JustifiedActionProps {
  /** The REAL underlying resource the write lands on (e.g. `transactions`, `users`). */
  resource: string;
  capability: Capability;
  id: Identifier;
  label: string;
  color?: 'primary' | 'success' | 'error' | 'warning' | 'inherit';
  dialogTitle: string;
  dialogIntro: string;
  /** When set, the dialog also collects a member id (clickless credit). */
  needsMember?: boolean;
  /** When set, the dialog offers a structured reason-code select (the first option
   *  is the default selection). The chosen value is passed to `buildData`. */
  reasonCodes?: readonly ReasonCodeOption[];
  /** When set, the dialog offers a period select (the first option is the default).
   *  The chosen length in days is passed to `buildData`. */
  periods?: readonly PeriodOption[];
  /** Build the write patch from the captured fields. */
  buildData: (
    justification: string,
    memberId: number | null,
    reasonCode: string | null,
    periodDays: number | null,
  ) => Record<string, unknown>;
  previousData: Record<string, unknown>;
  successMessage: string;
}

export const JustifiedAction = ({
  resource,
  capability,
  id,
  label,
  color = 'primary',
  dialogTitle,
  dialogIntro,
  needsMember = false,
  reasonCodes,
  periods,
  buildData,
  previousData,
  successMessage,
}: JustifiedActionProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justification, setJustification] = useState('');
  const [memberId, setMemberId] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [periodDays, setPeriodDays] = useState<number | ''>(() =>
    periods && periods.length > 0 ? periods[0].value : '',
  );

  const memberValid = !needsMember || /^\d+$/.test(memberId.trim());
  const reasonValid = !reasonCodes || reasonCode.trim().length > 0;
  const periodValid = !periods || periodDays !== '';
  const canSubmit =
    justification.trim().length > 0 &&
    memberValid &&
    reasonValid &&
    periodValid &&
    !loading;

  const reset = () => {
    setJustification('');
    setMemberId('');
    setReasonCode('');
    setPeriodDays(periods && periods.length > 0 ? periods[0].value : '');
  };

  const onConfirm = async () => {
    setLoading(true);
    try {
      await performStatusTransition({
        dataProvider,
        role,
        capability,
        resource,
        id,
        data: buildData(
          justification.trim(),
          needsMember ? Number(memberId.trim()) : null,
          reasonCodes ? reasonCode : null,
          periods && periodDays !== '' ? Number(periodDays) : null,
        ),
        previousData,
      });
      notify(successMessage, { type: 'success' });
      setOpen(false);
      setLoading(false);
      reset();
      refresh();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button variant="contained" color={color} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{dialogIntro}</DialogContentText>
          {needsMember && (
            <TextField
              label="Member id"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              error={memberId.length > 0 && !memberValid}
              helperText="The member this transaction belongs to"
            />
          )}
          {reasonCodes && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id={`reason-code-${id}`}>Reason code</InputLabel>
              <Select
                labelId={`reason-code-${id}`}
                label="Reason code"
                value={reasonCode}
                inputProps={{ 'data-testid': 'justified-reason-code' }}
                onChange={(e) => setReasonCode(e.target.value)}
              >
                {reasonCodes.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {periods && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id={`period-${id}`}>Suspension period</InputLabel>
              <Select
                labelId={`period-${id}`}
                label="Suspension period"
                value={periodDays === '' ? '' : String(periodDays)}
                inputProps={{ 'data-testid': 'justified-period' }}
                onChange={(e) => setPeriodDays(Number(e.target.value))}
              >
                {periods.map((opt) => (
                  <MenuItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label="Justification"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            helperText="Recorded with the action, for the audit trail"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={color}
            disabled={!canSubmit}
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
